import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LinkIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon,
  ArrowPathIcon,
  ArrowLeftIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

import {
  listConnections,
  listAvailableServers,
  initiateConsent,
  completeManualConsent,
  disconnect,
  setDefaultAccount,
  setToolDiscoveryConsent,
  AccountLabelConflictError,
  type EgressConnection,
  type AvailableEgressServer,
} from '../utils/egressAuth';
import { isSafeUrl } from '../utils/safeUrl';
import { formatRelativeTime, formatAbsoluteDate } from '../utils/dateUtils';

// How long to keep polling for a connection to turn "active" after the user
// opens the provider consent tab (connect or reconnect). The user completes
// consent in a separate tab with no way to signal this page directly, so
// short polling is the only way to reflect success/failure without a manual
// page refresh.
const CONNECT_POLL_INTERVAL_MS = 3000;
const CONNECT_POLL_MAX_ATTEMPTS = 40; // ~2 minutes

// Fast-scan color swatches for distinguishing sibling accounts on the same
// server (Firefox Multi-Account-Containers-style cue) — the label is the
// precise identifier, the swatch is the glance-level one.
const ACCOUNT_SWATCH_COLORS = [
  'bg-purple-500',
  'bg-blue-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
  'bg-fuchsia-500',
  'bg-lime-500',
];

function swatchColorFor(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return ACCOUNT_SWATCH_COLORS[Math.abs(hash) % ACCOUNT_SWATCH_COLORS.length];
}

function groupKey(provider: string, serverPath: string): string {
  return `${provider}:${serverPath}`;
}

/** The account an agent's tool call would automatically use for this group:
 * the legacy (account_id === null) entry if present (checked first on vend,
 * regardless of any sibling's is_default flag), else the sibling marked
 * is_default, else the single entry if there is exactly one, else none
 * (ambiguous — mirrors EgressAuthService.get_valid_token's resolution). */
function effectiveDefaultAccountId(group: EgressConnection[]): string | null | undefined {
  const legacy = group.find(c => c.account_id === null);
  if (legacy) return null;
  const marked = group.find(c => c.is_default);
  if (marked) return marked.account_id;
  if (group.length === 1) return group[0].account_id;
  return undefined;
}

/**
 * Connected Accounts: the end-user surface for the per-user egress credential
 * vault. Lists linked third-party accounts and lets the user connect a new one
 * (opens the provider consent in a new tab) or disconnect. Discoverable BEFORE
 * the first-use tool-call error so users can self-serve.
 */
const ConnectedAccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const [connections, setConnections] = useState<EgressConnection[]>([]);
  const [available, setAvailable] = useState<AvailableEgressServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [serverPath, setServerPath] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [copied, setCopied] = useState(false);
  // Non-null while polling for a specific (provider, server_path) to turn
  // "active" after the user opens a consent tab; drives the pending/success
  // banner. Cleared on success, timeout, or unmount.
  const [pendingConnection, setPendingConnection] = useState<{
    provider: string;
    serverPath: string;
    accountId: string | null;
  } | null>(null);
  const [connectResult, setConnectResult] = useState<'success' | 'timeout' | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  // Non-null while a manual-completion provider (e.g. Plane) is waiting for
  // the user to paste back the address their browser failed to load.
  const [manualCompletion, setManualCompletion] = useState<{
    provider: string;
    serverPath: string;
  } | null>(null);
  const [manualPasteInput, setManualPasteInput] = useState('');
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualError, setManualError] = useState('');
  // Non-null while showing the "heads up, you'll see a broken page" notice
  // for a manual-completion provider, BEFORE the consent tab opens -- so the
  // user reads it before they're staring at a failed-to-load page, not after.
  const [manualHeadsUp, setManualHeadsUp] = useState<{
    provider: string;
    serverPath: string;
    authorizeUrl: string;
  } | null>(null);
  // Non-null while the inline "connect another account" form is open for one
  // server group (provider, server_path).
  const [addingAccountFor, setAddingAccountFor] = useState<{
    provider: string;
    serverPath: string;
  } | null>(null);
  const [newAccountLabel, setNewAccountLabel] = useState('');
  const [addAccountError, setAddAccountError] = useState('');
  const [addingAccountBusy, setAddingAccountBusy] = useState(false);
  const [settingDefaultFor, setSettingDefaultFor] = useState<string | null>(null);
  const [togglingConsentFor, setTogglingConsentFor] = useState<string | null>(null);

  // The gateway callback (redirect) URL that must be registered in each
  // third-party OAuth app. Served by this same host through nginx, so it is
  // derived from the current origin.
  const callbackUrl = `${window.location.origin}/oauth2/egress/callback`;

  const handleCopyCallback = async () => {
    try {
      await navigator.clipboard.writeText(callbackUrl);
    } catch {
      // Clipboard API unavailable (e.g. non-HTTPS/older browser); fall back.
      const ta = document.createElement('textarea');
      ta.value = callbackUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    // "Copied" confirmation clears after 5 seconds.
    window.setTimeout(() => setCopied(false), 5000);
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [conns, avail] = await Promise.all([
        listConnections(),
        listAvailableServers(),
      ]);
      setConnections(conns);
      setAvailable(avail);
    } catch {
      setError('Could not load connections.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Stop any in-flight poll on unmount so it never sets state on a gone page.
  useEffect(() => {
    return () => {
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current);
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const startPollingForActive = useCallback(
    (provider: string, path: string, accountId: string | null) => {
      stopPolling();
      setConnectResult(null);
      setPendingConnection({ provider, serverPath: path, accountId });
      let attempts = 0;
      pollTimerRef.current = window.setInterval(async () => {
        attempts += 1;
        try {
          const conns = await listConnections();
          setConnections(conns);
          const match = conns.find(
            c => c.provider === provider && c.server_path === path && c.account_id === accountId
          );
          if (match?.status === 'active') {
            stopPolling();
            setPendingConnection(null);
            setConnectResult('success');
            return;
          }
        } catch {
          // Transient fetch failure; keep polling until attempts run out.
        }
        if (attempts >= CONNECT_POLL_MAX_ATTEMPTS) {
          stopPolling();
          setPendingConnection(null);
          setConnectResult('timeout');
        }
      }, CONNECT_POLL_INTERVAL_MS);
    },
    [stopPolling]
  );

  const openConsent = async (
    path: string,
    provider?: string,
    accountLabel?: string,
    mode: 'new' | 'reconnect' = 'new'
  ): Promise<void> => {
    const { authorizeUrl, manualCompletionRequired, accountId } = await initiateConsent(
      path,
      accountLabel,
      mode
    );
    // The authorize URL derives from per-server egress OAuth config, so treat
    // it as untrusted: only open http/https, never a javascript:/data: scheme
    // that would execute in the user's session on connect.
    if (!isSafeUrl(authorizeUrl)) {
      setError(`Could not start a connection for "${path}": the provider returned an unsafe authorization URL.`);
      return;
    }
    if (manualCompletionRequired && provider) {
      // Show the "you'll see a broken page, that's expected" notice BEFORE
      // opening the tab, not after -- a user who sees the failed-to-load page
      // first, with no warning, is liable to panic and close it before ever
      // reading the paste-code instructions.
      setManualHeadsUp({ provider, serverPath: path, authorizeUrl });
      return;
    }
    // Open the provider consent in a new tab.
    window.open(authorizeUrl, '_blank', 'noopener,noreferrer');
    if (provider) {
      startPollingForActive(provider, path, accountId);
    }
  };

  const handleContinueManualHeadsUp = () => {
    if (!manualHeadsUp) return;
    window.open(manualHeadsUp.authorizeUrl, '_blank', 'noopener,noreferrer');
    setManualError('');
    setManualPasteInput('');
    setManualCompletion({ provider: manualHeadsUp.provider, serverPath: manualHeadsUp.serverPath });
    setManualHeadsUp(null);
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualCompletion) return;
    const pasted = manualPasteInput.trim();
    if (!pasted) return;
    setManualSubmitting(true);
    setManualError('');
    try {
      await completeManualConsent(manualCompletion.serverPath, pasted);
      setManualCompletion(null);
      setManualPasteInput('');
      setConnectResult('success');
      await refresh();
    } catch {
      setManualError(
        'Could not complete the connection with that address. Make sure you copied the ' +
          'full address from the browser tab (including ?code=...&state=...) and try again.'
      );
    } finally {
      setManualSubmitting(false);
    }
  };

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    const path = serverPath.trim();
    if (!path) return;
    setConnecting(true);
    setError('');
    try {
      const server = available.find(s => s.server_path === path);
      await openConsent(path, server?.provider);
    } catch {
      setError(`Could not start a connection for "${path}". Check the server path.`);
    } finally {
      setConnecting(false);
    }
  };

  const handleReconnect = async (conn: EgressConnection) => {
    setError('');
    try {
      // Pass this connection's own label with mode 'reconnect' so it repairs
      // THIS account instead of creating an unintended sibling.
      await openConsent(conn.server_path, conn.provider, conn.account_label ?? undefined, 'reconnect');
    } catch {
      setError(`Could not start a reconnection for ${conn.provider} on ${conn.server_path}.`);
    }
  };

  const handleDisconnect = async (conn: EgressConnection) => {
    setError('');
    try {
      await disconnect(conn.provider, conn.server_path, conn.account_id);
      await refresh();
    } catch {
      setError(`Could not disconnect ${conn.provider} for ${conn.server_path}.`);
    }
  };

  const handleSetDefault = async (conn: EgressConnection) => {
    setError('');
    const key = groupKey(conn.provider, conn.server_path);
    setSettingDefaultFor(key);
    try {
      await setDefaultAccount(conn.provider, conn.server_path, conn.account_id);
      await refresh();
    } catch {
      setError(`Could not set ${conn.account_label || conn.provider} as default.`);
    } finally {
      setSettingDefaultFor(null);
    }
  };

  const handleToggleToolDiscoveryConsent = async (conn: EgressConnection) => {
    setError('');
    const entryKey = `${conn.provider}:${conn.server_path}:${conn.account_id ?? ''}`;
    setTogglingConsentFor(entryKey);
    try {
      await setToolDiscoveryConsent(
        conn.provider,
        conn.server_path,
        conn.account_id,
        !conn.consents_to_tool_discovery
      );
      // Re-fetch rather than optimistically flip local state, so a failed
      // call leaves the checkbox showing the real, unchanged server state
      // instead of a false "checked" that never actually saved.
      await refresh();
    } catch {
      setError(`Could not update tool-discovery consent for ${conn.provider} on ${conn.server_path}.`);
    } finally {
      setTogglingConsentFor(null);
    }
  };

  const handleStartAddAccount = (provider: string, serverPath: string, existingCount: number) => {
    setAddAccountError('');
    // Auto-suggested placeholder the user is nudged to rename (auto-numbered
    // labels alone become unusable once there are more than a couple).
    setNewAccountLabel(`${provider} #${existingCount + 1}`);
    setAddingAccountFor({ provider, serverPath });
  };

  const handleSubmitAddAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addingAccountFor) return;
    const label = newAccountLabel.trim();
    if (!label) return;
    setAddingAccountBusy(true);
    setAddAccountError('');
    try {
      await openConsent(addingAccountFor.serverPath, addingAccountFor.provider, label, 'new');
      setAddingAccountFor(null);
      setNewAccountLabel('');
    } catch (err) {
      if (err instanceof AccountLabelConflictError) {
        setAddAccountError(err.message);
      } else {
        setAddAccountError(`Could not start a connection for "${label}". Try a different label.`);
      }
    } finally {
      setAddingAccountBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <button
        type="button"
        onClick={() => navigate('/')}
        className="flex items-center space-x-1 mb-4 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        <span>Back to Dashboard</span>
      </button>
      <div className="flex items-center space-x-3 mb-2">
        <LinkIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Connected Accounts</h1>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
        Link your third-party accounts (GitHub, Slack, Google, …) so MCP servers can act on your
        behalf. Connect an account here before using a server that requires it.
      </p>

      {/* Plain-language explainer for the "Let background discovery use this
          connection" checkbox on each connection below. Placed once, up
          front, since a small per-row caption is easy to skim past and the
          mechanism (why a checkbox exists at all) isn't self-evident. */}
      <div className="mb-6 rounded-md border border-purple-200 dark:border-purple-900/50 bg-purple-50 dark:bg-purple-900/20 px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
        <p className="font-medium text-purple-800 dark:text-purple-300">
          What does "Let background discovery use this connection" mean?
        </p>
        <p className="mt-1">
          Each server below periodically checks what tools it offers, so the registry can show an
          accurate tool list. That check needs someone's connection to run -- it can't use just
          anyone's. Checking this box on one of your connections volunteers it for that one
          purpose. It does <span className="font-medium">not</span> let other people use your
          account, and it has no effect on how your own tool calls work.
        </p>
      </div>

      {/* Callback URL: must be registered as the redirect/callback URL in each
          third-party OAuth app (GitHub, Slack, Atlassian, …). Kept compact. */}
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400 mb-6">
        <span>OAuth redirect/callback URL (register in each provider's OAuth app):</span>
        <code className="break-all text-purple-700 dark:text-purple-300">{callbackUrl}</code>
        <button
          type="button"
          onClick={handleCopyCallback}
          className="inline-flex items-center text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 focus:outline-none"
          aria-label="Copy callback URL"
          title={copied ? 'Copied' : 'Copy'}
        >
          {copied ? (
            <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
          ) : (
            <ClipboardDocumentIcon className="h-4 w-4" />
          )}
        </button>
        {copied && <span className="text-green-600 dark:text-green-400">Copied</span>}
      </p>

      {error && (
        <div className="flex items-center space-x-2 mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {pendingConnection && (
        <div className="flex items-center space-x-2 mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
          <ArrowPathIcon className="h-5 w-5 flex-shrink-0 animate-spin" />
          <span className="text-sm">
            Waiting for {pendingConnection.provider} consent to complete for{' '}
            {pendingConnection.serverPath}…
          </span>
        </div>
      )}

      {manualHeadsUp && (
        <div className="mb-4 p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/30 text-yellow-900 dark:text-yellow-200">
          <p className="text-sm mb-3">
            <strong>Heads up:</strong> after you approve access for{' '}
            <span className="capitalize">{manualHeadsUp.provider}</span>, the new tab will show a
            page that fails to load. <strong>This is expected, not a bug in our system</strong> —
            it&apos;s a limitation on {manualHeadsUp.provider}&apos;s own login page, which can
            only redirect to a local address instead of our site directly. The code you need will
            still be visible in that failed page&apos;s address bar. Once you see it, come back
            here and paste the full address into the box that appears.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleContinueManualHeadsUp}
              className="flex items-center space-x-2 px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              <span>Continue to {manualHeadsUp.provider}</span>
            </button>
            <button
              type="button"
              onClick={() => setManualHeadsUp(null)}
              className="px-3 py-2 rounded-md text-sm text-yellow-800 dark:text-yellow-300 hover:bg-yellow-100 dark:hover:bg-yellow-900/50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {manualCompletion && (
        <form
          onSubmit={handleManualSubmit}
          className="mb-4 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-200"
        >
          <p className="text-sm mb-2">
            After you approve access for <span className="capitalize">{manualCompletion.provider}</span>, the
            browser tab will show a page that fails to load (this is expected). Copy the full
            address from that tab&apos;s address bar and paste it below.
          </p>
          <div className="flex items-end gap-3">
            <input
              type="text"
              value={manualPasteInput}
              onChange={e => setManualPasteInput(e.target.value)}
              placeholder="http://localhost:8400/callback?code=...&state=..."
              className="flex-1 px-3 py-2 rounded-md border border-blue-300 dark:border-blue-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              type="submit"
              disabled={manualSubmitting || !manualPasteInput.trim()}
              className="px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {manualSubmitting ? 'Connecting…' : 'Submit'}
            </button>
            <button
              type="button"
              onClick={() => setManualCompletion(null)}
              className="px-3 py-2 rounded-md text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50"
            >
              Cancel
            </button>
          </div>
          {manualError && <p className="text-sm text-red-600 dark:text-red-400 mt-2">{manualError}</p>}
        </form>
      )}

      {connectResult === 'success' && (
        <div className="flex items-center space-x-2 mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300">
          <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">Connected successfully.</span>
        </div>
      )}

      {connectResult === 'timeout' && (
        <div className="flex items-center space-x-2 mb-4 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">
            Still waiting on that connection. If you completed consent in the other tab, refresh
            this page to see it.
          </span>
        </div>
      )}

      {/* Connect a new account: pick from the egress-enabled servers the user
          can access (no need to know/type a raw server path). */}
      <form
        onSubmit={handleConnect}
        className="flex items-end gap-3 mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-800"
      >
        <div className="flex-1">
          <label
            htmlFor="egress-server"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Server requiring per-user authentication
          </label>
          <select
            id="egress-server"
            value={serverPath}
            onChange={e => setServerPath(e.target.value)}
            disabled={available.length === 0}
            className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          >
            <option value="">
              {available.length === 0
                ? 'No servers require per-user authentication'
                : 'Select a server…'}
            </option>
            {available.map(s => (
              <option key={s.server_path} value={s.server_path}>
                {s.server_name} ({s.provider}) — {s.server_path}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={connecting || !serverPath.trim()}
          className="flex items-center space-x-2 px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          <span>{connecting ? 'Opening…' : 'Connect'}</span>
        </button>
      </form>

      {/* Existing connections, grouped by (provider, server_path) so multiple
          accounts on one server render together. */}
      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : connections.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No connected accounts yet.</p>
      ) : (
        <div className="space-y-4">
          {Object.values(
            connections.reduce<Record<string, EgressConnection[]>>((acc, conn) => {
              const key = groupKey(conn.provider, conn.server_path);
              (acc[key] ||= []).push(conn);
              return acc;
            }, {})
          ).map(group => {
            const { provider, server_path: path } = group[0];
            const key = groupKey(provider, path);
            const defaultId = effectiveDefaultAccountId(group);
            const sorted = [...group].sort((a, b) => {
              if (a.account_id === null) return -1;
              if (b.account_id === null) return 1;
              return (a.account_label || '').localeCompare(b.account_label || '');
            });
            return (
              <div key={key} className="rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2 px-4 pt-3 pb-1">
                  <span className="font-medium text-gray-900 dark:text-white capitalize">
                    {provider}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{path}</span>
                </div>
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sorted.map(conn => {
                    const swatchKey = conn.account_id ?? '__legacy__';
                    const isDefault = defaultId !== undefined && conn.account_id === defaultId;
                    return (
                      <li
                        key={`${key}:${conn.account_id ?? ''}`}
                        className="flex items-start justify-between gap-4 p-4"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center space-x-2">
                            {group.length > 1 && (
                              <span
                                className={`inline-block h-2.5 w-2.5 rounded-full flex-shrink-0 ${swatchColorFor(swatchKey)}`}
                                aria-hidden="true"
                              />
                            )}
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {conn.account_label || (conn.account_id === null ? 'Primary' : conn.account_id)}
                            </span>
                            {isDefault && group.length > 1 && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                                Default
                              </span>
                            )}
                            {conn.status !== 'active' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300">
                                {conn.status}
                              </span>
                            )}
                            {/* AGENTGOV-164: derived from what the provider itself
                                granted, not a self-declared label. 'unknown' is
                                shown with no color implying safety either way. */}
                            {conn.credential_scope === 'verified_read_only' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300" title="Every scope this provider granted is on its known read-only allowlist.">
                                Read-only (verified)
                              </span>
                            )}
                            {conn.credential_scope === 'verified_write_capable' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300" title="At least one granted scope allows writing/changing data.">
                                Write-capable
                              </span>
                            )}
                          </div>
                          {conn.scopes.length > 0 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                              {conn.scopes.join(', ')}
                            </div>
                          )}
                          <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 space-x-3">
                            {conn.expires_at && (
                              <span title={formatAbsoluteDate(conn.expires_at)}>
                                Expires {formatRelativeTime(conn.expires_at)}
                              </span>
                            )}
                            {conn.last_refreshed_at && (
                              <span title={formatAbsoluteDate(conn.last_refreshed_at)}>
                                Last refreshed {formatRelativeTime(conn.last_refreshed_at)}
                              </span>
                            )}
                          </div>
                          <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                            <input
                              type="checkbox"
                              checked={conn.consents_to_tool_discovery}
                              disabled={
                                togglingConsentFor ===
                                `${conn.provider}:${conn.server_path}:${conn.account_id ?? ''}`
                              }
                              onChange={() => handleToggleToolDiscoveryConsent(conn)}
                              className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span>Let background discovery use this connection</span>
                          </label>
                          <p className="mt-1 text-[11px] leading-snug text-gray-400 dark:text-gray-500">
                            Lets the registry keep this server's tool list accurate for everyone,
                            using your connection. Does not give other users access to your account.
                          </p>
                        </div>
                        <div className="flex flex-shrink-0 items-center space-x-2">
                          {group.length > 1 && !isDefault && (
                            <button
                              onClick={() => handleSetDefault(conn)}
                              disabled={settingDefaultFor === key}
                              className="px-3 py-1.5 rounded-md text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                              aria-label={`Set ${conn.account_label || conn.provider} as default`}
                            >
                              Set as default
                            </button>
                          )}
                          {conn.status === 'refresh_failed' && (
                            <button
                              onClick={() => handleReconnect(conn)}
                              className="flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 focus:outline-none focus:ring-2 focus:ring-purple-500"
                              aria-label={`Reconnect ${conn.provider} for ${conn.server_path}`}
                            >
                              <ArrowPathIcon className="h-4 w-4" />
                              <span>Reconnect</span>
                            </button>
                          )}
                          <button
                            onClick={() => handleDisconnect(conn)}
                            className="flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-red-500"
                            aria-label={`Disconnect ${conn.provider} for ${conn.server_path}`}
                          >
                            <TrashIcon className="h-4 w-4" />
                            <span>Disconnect</span>
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {/* Connect another account: only relevant once this server has
                    at least one connection already. Distinct from Reconnect,
                    which repairs an existing account instead of adding one. */}
                {addingAccountFor?.provider === provider && addingAccountFor.serverPath === path ? (
                  <form
                    onSubmit={handleSubmitAddAccount}
                    className="flex items-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
                  >
                    <div className="flex-1">
                      <label
                        htmlFor={`account-label-${key}`}
                        className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Account label
                      </label>
                      <input
                        id={`account-label-${key}`}
                        type="text"
                        value={newAccountLabel}
                        onChange={e => setNewAccountLabel(e.target.value)}
                        placeholder="e.g. Nomad Nest Pty Ltd"
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={addingAccountBusy || !newAccountLabel.trim()}
                      className="flex items-center space-x-2 px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      <span>{addingAccountBusy ? 'Opening…' : 'Connect'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAddingAccountFor(null)}
                      className="px-3 py-2 rounded-md text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    {addAccountError && (
                      <p className="text-xs text-red-600 dark:text-red-400 w-full">{addAccountError}</p>
                    )}
                  </form>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleStartAddAccount(provider, path, group.length)}
                    className="w-full text-left px-4 py-2 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 border-t border-gray-200 dark:border-gray-700 focus:outline-none"
                  >
                    + Connect another account
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConnectedAccountsPage;
